/**
 * Auth + Premium global context for BetCracker freemium.
 *
 * Wraps the entire app and exposes:
 *   - user                — full user object (or null)
 *   - isAuthenticated     — derived
 *   - isPremium           — canonical premium flag (server-decided)
 *   - login/register/logout
 *   - claimAccessCode     — migrate legacy BCRK-XXXX → grandfathered Premium
 *   - refreshMe           — re-fetch /me (used after LemonSqueezy checkout)
 *
 * Token is stored in localStorage("bc_jwt"). The legacy access-code
 * session ("bc_access_session") is preserved untouched for backward
 * compatibility with screens that still rely on it.
 */
import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "bc_jwt";

// Global axios interceptor: attach JWT Bearer AND/OR the legacy access-code
// header to ALL requests targeting our API. This way components don't need
// to remember the auth glue: both freemium JWT users and legacy BCRK-XXXX
// holders get their Premium credentials forwarded automatically.
axios.interceptors.request.use((config) => {
  try {
    const url = (config?.url || "") + "";
    if (url.includes(API)) {
      const t = localStorage.getItem(TOKEN_KEY);
      if (t && !config.headers?.Authorization) {
        config.headers = { ...(config.headers || {}), Authorization: `Bearer ${t}` };
      }
      // Legacy access code: stored as JSON {code, expires_at} under
      // "bc_access_session" by AccessGate. Forward it as X-Access-Code so
      // backend's require_premium can grant grandfathered access.
      if (!config.headers?.["X-Access-Code"]) {
        try {
          const sess = JSON.parse(localStorage.getItem("bc_access_session") || "null");
          if (sess?.code) {
            config.headers = { ...(config.headers || {}), "X-Access-Code": sess.code };
          }
        } catch { /* noop */ }
      }
    }
  } catch { /* noop */ }
  return config;
});

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const getToken = () => {
    try { return localStorage.getItem(TOKEN_KEY) || null; }
    catch { return null; }
  };

  const refreshMe = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setUser(null);
      return null;
    }
    try {
      const res = await axios.get(`${API}/users/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(res.data);
      return res.data;
    } catch (err) {
      // 401 → token scaduto/invalido
      if (err?.response?.status === 401) {
        try { localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
        setUser(null);
      }
      return null;
    }
  }, []);

  useEffect(() => {
    (async () => {
      await refreshMe();
      setLoading(false);
    })();
  }, [refreshMe]);

  const _store = (data) => {
    try { localStorage.setItem(TOKEN_KEY, data.token); } catch { /* ignore */ }
    setUser(data.user);
    return data.user;
  };

  const register = async (email, password) => {
    const res = await axios.post(`${API}/users/register`, { email, password });
    return _store(res.data);
  };

  const login = async (email, password) => {
    const res = await axios.post(`${API}/users/login`, { email, password });
    return _store(res.data);
  };

  const logout = () => {
    try { localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
    setUser(null);
  };

  const claimAccessCode = async (code) => {
    const token = getToken();
    if (!token) throw new Error("Devi essere loggato");
    const res = await axios.post(
      `${API}/users/claim-access-code`,
      { access_code: code },
      { headers: { Authorization: `Bearer ${token}` } },
    );
    setUser(res.data.user);
    return res.data.user;
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    isPremium: !!user?.is_premium,
    premiumStatus: user?.premium_status || "none",
    premiumSource: user?.premium_source || null,
    register,
    login,
    logout,
    refreshMe,
    claimAccessCode,
    getToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth() must be used inside <AuthProvider>");
  return ctx;
}

/**
 * Convenience hook for components that ONLY care about the premium flag.
 * Returns: { isPremium, status, source, loading }
 */
export function usePremium() {
  const { isPremium, premiumStatus, premiumSource, loading } = useAuth();
  return { isPremium, status: premiumStatus, source: premiumSource, loading };
}
